import React from 'react';
import { Route } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { ShareBreakdown } from './ShareBreakdown';
import { ACCESS_METHOD_LABELS, type AccessMethod } from '../../utils/accessMethods';
import type { BucketRow } from '../../types/traffic';
interface AccessMethodsCardProps {
  methods: BucketRow[];
}
function label(code: string): string {
  return ACCESS_METHOD_LABELS[code as AccessMethod] ?? code;
}

/**
 * How people are reaching the site. Beyond plain consumer ISP traffic, each method
 * carries a different operational meaning — satellite and Tor imply constrained or
 * filtered access, cloud desktops imply remote working setups.
 */
export function AccessMethodsCard({
  methods
}: AccessMethodsCardProps) {
  return <Card data-id="element-6" data-mp="194ix6zaruqov">
      <CardHeader data-id="element-7" data-mp="19525rpej82q8">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-8" data-mp="195kcxh2qg1cx">
          <CardTitle className="flex items-center gap-2" data-id="element-9" data-mp="14bpusi2rzufy">
            <Route className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-10" data-mp="2juj2pg5cu546" />
            How people reach the internet
          </CardTitle>
          <Badge variant="outline" data-id="element-11" data-mp="2ot42jegp2ms3">Heuristic</Badge>
        </div>
        <CardDescription data-id="element-12" data-mp="2b1fupndhakgx">
          Access method inferred from browser fingerprint and network operator — Tor, satellite,
          residential proxy, cloud desktop, VPN, corporate, or consumer ISP
        </CardDescription>
      </CardHeader>
      <CardContent data-id="element-13" data-mp="216e5ysn47kev">
        {methods.length > 0 ? <ShareBreakdown rows={methods.map(method => ({
        label: label(method.label),
        share: method.share,
        detail: (method.visitors ?? method.requests).toLocaleString()
      }))} data-id="element-14" data-mp="264flx4p8v3m4" /> : <p className="py-4 text-xs leading-relaxed text-muted-foreground" data-id="element-15" data-mp="3mhnx9oswtvsl">
            No access method classified yet. This needs the network operator from the geo lookup, so
            it fills in as geography resolves.
          </p>}

        <div className="mt-6 space-y-2 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground" data-id="element-16" data-mp="13blcq8vmfed3">
          <p data-id="element-17" data-mp="yae1u0y6lz27">
            <span className="font-medium text-foreground" data-id="element-18" data-mp="ye1wx4j9ytj1">Tor Browser</span> is detected from its own
            anti-fingerprinting — a UTC time zone, en-US locale, and no Network Information API at
            once.{' '}
            <span className="font-medium text-foreground" data-id="element-19" data-mp="1m11mlmt74j66">Satellite</span> and{' '}
            <span className="font-medium text-foreground" data-id="element-20" data-mp="1hzogdbm6ydyq">residential proxy</span> come from the
            network operator, so they only cover vendors on the known list.
          </p>
          <p data-id="element-21" data-mp="1d6vauliwf1ll">
            All of these are population-level heuristics. They are wrong for individuals — a
            developer on a cloud desktop and someone routing around a filter look identical from
            here.
          </p>
        </div>
      </CardContent>
    </Card>;
}